<script setup lang="ts">
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { storeToRefs } from "pinia";
import { useSessionDetailsStore } from "~/store/sessions/session-details-store";
import { usePagination } from "~/composables/paginations/usePagination";
import type { PropType } from "vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";

const sessionStore = useSessionsStore();
const { showHideKpi } = storeToRefs(sessionStore);
const sessionDetailsStore = useSessionDetailsStore();
const { endTrainingSession } = storeToRefs(sessionDetailsStore);

const props = defineProps({
  headersNav: {
    type: Array as PropType<any[]>,
  },
  showHideBlock: {
    type: Boolean,
    default: false,
  },
  resetNav: {
    type: Number,
  },
  tabSelected: {
    type: String,
  },
  tabFilterKey: {
    type: String, // values (name, filter)
    required: false,
    default: "name",
  },
  filterKey: {
    type: String,
    default: "filter",
  },
  setPaginateOpts: {
    type: Boolean,
    default: true,
  },
  isLoading: {
    type: Boolean,
    required: false,
    default: false,
  },
  paginationKey: {
    type: String,
    required: false,
  },
});

const paginate = usePagination(props.paginationKey);

const emit = defineEmits(["tabNavigate"]);

function isSelected(filter: any) {
  return props.tabSelected === filter;
}

function selectNav(nav: Record<string, any>) {
  if (props.headersNav && nav) {
    if (props.setPaginateOpts === true) {
      paginate.paginateOptions.value.filter = props.tabFilterKey === "filter" ? nav.filter : nav.name;
    }

    if (!props.isLoading) emit("tabNavigate", nav);
  }
}

onMounted(() => {
  if (!props.tabSelected && props.headersNav) {
    const firstNav = props.headersNav?.find(Boolean);
    selectNav(firstNav);
  }
  if (endTrainingSession.value.showHabilitation) {
    const nav = props?.headersNav?.find((nav: Record<string, any>) => nav.name === "Habilitations");
    if (nav) {
      selectNav(nav);
    }
  } else if (endTrainingSession.value.showFileUpload) {
    const nav = props?.headersNav?.find((nav: Record<string, any>) => nav.name === "Documents");
    if (nav) {
      selectNav(nav);
    }
  }
});

watch(
  () => endTrainingSession.value.showFileUpload,
  () => {
    if (endTrainingSession.value.showFileUpload) {
      const nav = props?.headersNav?.find((nav: Record<string, any>) => nav.name === "Documents");
      if (nav) {
        selectNav(nav);
      }
    }
  },
);

watch(
  () => endTrainingSession.value.showHabilitation,
  () => {
    if (endTrainingSession.value.showHabilitation) {
      const nav = props?.headersNav?.find((nav: Record<string, any>) => nav.name === "Habilitations");
      if (nav) {
        selectNav(nav);
      }
    }
  },
);

watch(
  () => props.resetNav,
  () => {
    const nav = props.headersNav?.find(Boolean);
    if (nav) selectNav(nav);
  },
);
</script>

<template>
  <div class="border-b-[2px] border-stroke-color">
    <div class="flex px-3 place-content-between">
      <div class="flex flex-row container-menu">
        <div
          v-for="nav in headersNav"
          :key="`key-${nav.name}`"
          class="grid_nav_button nav mx-[8px]"
          :class="{ nav_selected: isSelected(nav[props.filterKey as any] as any), hidden_mobile: nav.hiddenMobile }"
          @click="selectNav(nav)"
        >
          <div class="grid_nav_text body_regular link" :data-border-right="nav.borderRight" :data-title="nav.title">
            {{ nav.name }}{{ nav.nb || !isNaN(nav.nb!) ? ` (${nav.nb})` : "" }}
          </div>
        </div>
      </div>

      <div v-if="props.showHideBlock" class="grid_nav_button">
        <div v-if="!showHideKpi" class="flex justify-end grid_nav_text_hiding_kpi">
          <button class="button_secondary_default flex justify-end showHideBlock gap-[8px]" @click="showHideKpi = true">
            <SvgIcon class="w-[16px] h-[16px]" name="hide"></SvgIcon>
            <span class="justify-end body_regular">Masquer totaux</span>
          </button>
        </div>
        <div v-if="showHideKpi" class="flex justify-end grid_nav_text_hiding_kpi">
          <button
            class="button_secondary_default items-center flex justify-end showHideBlock gap-[8px]"
            @click="showHideKpi = false"
          >
            <SvgIcon class="w-[16px] h-[16px]" name="see"></SvgIcon>
            <span class="justify-end body_regular">Afficher totaux</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "assets/scss/texts";

.nav {
  @apply gap-[24px] border-b-[1px] border-stroke-color;
}

.hidden_mobile {
  @apply hidden sm:flex;
}

.grid_nav_button {
  @apply py-[6px] cursor-pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
}

.grid_nav_text {
  @apply px-[8px] py-[8px] text-center relative leading-[20px];
  color: var(--text-secondary-color);

  &[data-border-right="true"]::after {
    content: "";
    position: absolute;
    right: -8px;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: var(--grey-medium-light);
  }

  &[data-title="true"] {
    @apply body_med uppercase;
  }
}

.nav_selected {
  border-bottom: 2px solid var(--primary-blue-color);

  .grid_nav_text {
    @apply text-primary-blue-color;
  }
}

.grid_nav_button:not(.nav_selected):hover {
  .grid_nav_text {
    color: var(--text-main-color);
  }
}

.grid_nav_text_hiding_kpi {
  @apply flex flex-row gap-[0px] border-b-[0px] border-stroke-color px-[8px] py-[8px] text-center leading-[20px] text-secondary-color;

  &[data-border-right="true"] {
    border-right: 1px solid var(--grey-medium-light);
  }
}

.show_hide {
  color: var(--primary-blue-color);
  filter: invert(50%) sepia(36%) saturate(3981%) hue-rotate(185deg) brightness(103%) contrast(105%);
}

.show_hide:hover {
  text-decoration: underline;
}

@media (max-width: "768px") {
  .container-menu {
    @apply overflow-auto whitespace-nowrap w-full;
    -ms-overflow-style: none;
    /* IE and Edge */
    scrollbar-width: none;
    /* Firefox */
  }

  .container-menu::-webkit-scrollbar {
    display: none;
  }

  .link {
    @apply inline-block text-center;
    text-decoration: none;
  }
}
</style>
